import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { CreditCard, ChevronLeft, ChevronRight, CheckCircle, Clock, XCircle, Download, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';

const AdminTransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ status: '', plan: '', email: '', payment_method: '', transaction_id: '' });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { fetchTransactions(); }, [page]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filters.status) params.status = filters.status;
      if (filters.plan) params.plan = filters.plan;
      if (filters.email) params.email = filters.email;
      if (filters.payment_method) params.payment_method = filters.payment_method;
      if (filters.transaction_id) params.transaction_id = filters.transaction_id;
      const response = await adminAPI.getTransactions(page, params);
      setTransactions(response.data.transactions);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch { toast.error('Failed to fetch transactions'); }
    finally { setLoading(false); }
  };

  const handleFilter = () => { setPage(1); fetchTransactions(); };
  const clearFilters = () => { setFilters({ status: '', plan: '', email: '', payment_method: '', transaction_id: '' }); setPage(1); setTimeout(fetchTransactions, 0); };

  const handleExportCSV = () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('admin_token');
    window.open(`${API}/api/admin/transactions/export?token=${token}`, '_blank');
    toast.success('Downloading CSV...');
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'completed': return { color: 'text-[#1E8E3E]', bg: 'bg-[#E6F4EA]', icon: <CheckCircle className="w-3.5 h-3.5" /> };
      case 'failed': return { color: 'text-[#D93025]', bg: 'bg-[#FCE8E6]', icon: <XCircle className="w-3.5 h-3.5" /> };
      default: return { color: 'text-[#F9AB00]', bg: 'bg-[#FEF7E0]', icon: <Clock className="w-3.5 h-3.5" /> };
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="admin-transactions-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Payment Transactions</h1>
          <p className="text-[#5F6368]">{total} total transactions</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm hover:bg-gray-50 ${showFilters ? 'border-[#1A73E8] text-[#1A73E8]' : ''}`} data-testid="toggle-filters-btn">
            <Filter className="w-4 h-4" /> Filters
          </button>
          <button onClick={handleExportCSV} className="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm hover:bg-gray-50" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="dashboard-card" data-testid="filters-panel">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#5F6368] mb-1">Transaction ID</label>
              <input value={filters.transaction_id} onChange={e => setFilters({...filters, transaction_id: e.target.value})} placeholder="Search by ID" className="input-google py-1.5 text-sm" data-testid="filter-transaction-id" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5F6368] mb-1">Status</label>
              <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})} className="input-google py-1.5 text-sm">
                <option value="">All</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5F6368] mb-1">Plan</label>
              <select value={filters.plan} onChange={e => setFilters({...filters, plan: e.target.value})} className="input-google py-1.5 text-sm">
                <option value="">All</option>
                <option value="basic">Basic</option>
                <option value="pro">Pro</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5F6368] mb-1">Payment Method</label>
              <select value={filters.payment_method} onChange={e => setFilters({...filters, payment_method: e.target.value})} className="input-google py-1.5 text-sm">
                <option value="">All</option>
                <option value="stripe">Stripe</option>
                <option value="razorpay">Razorpay</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5F6368] mb-1">User Email</label>
              <input value={filters.email} onChange={e => setFilters({...filters, email: e.target.value})} placeholder="Search by email" className="input-google py-1.5 text-sm" />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleFilter} className="px-4 py-1.5 bg-[#1A73E8] text-white rounded-lg text-sm hover:bg-[#1557B0]" data-testid="apply-filters-btn">
              <Search className="w-4 h-4 inline mr-1" /> Apply
            </button>
            <button onClick={clearFilters} className="px-4 py-1.5 border rounded-lg text-sm hover:bg-gray-50">Clear</button>
          </div>
        </div>
      )}

      <div className="dashboard-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32"><div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" /></div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12"><CreditCard className="w-12 h-12 mx-auto text-[#DADCE0]" /><p className="mt-4 text-[#5F6368]">No transactions found</p></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#DADCE0]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Transaction ID</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">User</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Plan</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Amount</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Method</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn, i) => {
                    const statusConfig = getStatusConfig(txn.status);
                    const txId = txn.session_id || txn.order_id || txn.id || '';
                    return (
                      <tr key={txn.id || i} className="border-b border-[#DADCE0] hover:bg-[#F8F9FA]">
                        <td className="py-3 px-4 text-sm text-[#202124] font-mono">{txId.slice(0, 12)}...</td>
                        <td className="py-3 px-4">
                          <p className="text-sm text-[#202124]">{txn.user_name || ''}</p>
                          <p className="text-xs text-[#9AA0A6]">{txn.user_email || txn.user_id?.slice(0, 8)}</p>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            txn.plan === 'pro' ? 'bg-[#F3E8FD] text-[#9334E9]' : 'bg-[#E8F0FE] text-[#1A73E8]'
                          }`}>{txn.plan?.toUpperCase()}</span>
                        </td>
                        <td className="py-3 px-4 text-sm font-medium text-[#202124]">
                          {txn.currency?.toUpperCase() === 'INR' ? '₹' : '$'}{txn.amount}
                        </td>
                        <td className="py-3 px-4 text-sm text-[#5F6368] capitalize">{txn.payment_method}</td>
                        <td className="py-3 px-4">
                          <span className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded w-fit ${statusConfig.bg} ${statusConfig.color}`}>
                            {statusConfig.icon} {txn.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-[#5F6368]">{new Date(txn.created_at).toLocaleDateString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-[#DADCE0]">
                <p className="text-sm text-[#5F6368]">Page {page} of {totalPages}</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="p-2 border rounded hover:bg-gray-50 disabled:opacity-50"><ChevronLeft className="w-4 h-4" /></button>
                  <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="p-2 border rounded hover:bg-gray-50 disabled:opacity-50"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminTransactionsPage;
